import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { MapPinIcon, LeafIcon, AwardIcon, GlobeIcon } from 'lucide-react';
export function VineyardPage() {
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <main className="min-h-screen bg-cream pt-20" data-id="element-507">
      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[400px] flex items-center justify-center overflow-hidden" data-id="element-508">
        <div className="absolute inset-0 z-0" style={{
        backgroundImage: 'url("https://cdn.magicpatterns.com/uploads/34vhrQ1Y87uCN6XUamVsmg/IMG_0167.jpg")',
        backgroundPosition: 'center',
        backgroundSize: 'cover'
      }} data-id="element-509">
          <div className="absolute inset-0 bg-charcoal/50 mix-blend-multiply" data-id="element-510" />
          <div className="absolute inset-0 bg-gradient-to-b from-charcoal/40 via-transparent to-charcoal/80" data-id="element-511" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center" data-id="element-512">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          ease: 'easeOut'
        }} data-id="element-513">
            <span className="text-gold font-semibold tracking-widest uppercase text-sm mb-6 block" data-id="element-514">
              Our Vineyard Portfolio
            </span>
            <h1 className="font-playfair text-5xl md:text-6xl lg:text-7xl text-white font-bold leading-tight mb-6" data-id="element-515">
              Exceptional <span className="italic text-gold" data-id="element-516">Partnerships</span>
            </h1>
            <p className="text-lg md:text-xl text-cream-100 mb-10 max-w-2xl mx-auto font-light leading-relaxed" data-id="element-517">
              We partner with world-class vineyards to bring you exceptional
              wines for your custom label experience.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Featured Vineyard - Chateau de Chaintres */}
      <section className="py-24 bg-white" data-id="element-518">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-519">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="mb-12" data-id="element-520">
            <span className="text-wine font-semibold tracking-wider uppercase text-sm mb-4 block" data-id="element-521">
              Featured Partner
            </span>
            <h2 className="font-playfair text-4xl md:text-5xl text-charcoal font-bold mb-6" data-id="element-522">
              Chateau de{' '}
              <span className="italic text-wine font-normal" data-id="element-523">Chaintres</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16" data-id="element-524">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} data-id="element-525">
              <img src="https://cdn.magicpatterns.com/uploads/e9v7a9RpnimnqEK1EaUis4/dji_fly_20250529_213518_40_1748547873499_photo_optimized.jpg" alt="Chateau de Chaintres vineyard" className="w-full h-[500px] object-cover rounded-sm shadow-elevated" data-id="element-526" />
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="space-y-6" data-id="element-527">
              <div className="flex items-start gap-3" data-id="element-528">
                <MapPinIcon className="w-6 h-6 text-wine flex-shrink-0 mt-1" data-id="element-529" />
                <div data-id="element-530">
                  <h3 className="font-bold text-charcoal mb-2" data-id="element-531">Location</h3>
                  <p className="text-brown/70" data-id="element-532">
                    Loire Valley, France — one of the world's most prestigious
                    wine regions, known for elegant Cabernet Franc and Chenin
                    Blanc.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3" data-id="element-533">
                <LeafIcon className="w-6 h-6 text-wine flex-shrink-0 mt-1" data-id="element-534" />
                <div data-id="element-535">
                  <h3 className="font-bold text-charcoal mb-2" data-id="element-536">
                    Bio-Certified
                  </h3>
                  <p className="text-brown/70" data-id="element-537">
                    All wines are produced using organic and sustainable
                    practices, certified bio by French agricultural standards.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3" data-id="element-538">
                <AwardIcon className="w-6 h-6 text-wine flex-shrink-0 mt-1" data-id="element-539" />
                <div data-id="element-540">
                  <h3 className="font-bold text-charcoal mb-2" data-id="element-541">
                    Family Heritage
                  </h3>
                  <p className="text-brown/70" data-id="element-542">
                    A family-owned estate with generations of winemaking
                    expertise, crafting wines that balance tradition with modern
                    techniques.
                  </p>
                </div>
              </div>

              <div className="pt-4" data-id="element-543">
                <a href="https://chaintres.fr/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-6 py-3 bg-wine text-white rounded-sm font-medium hover:bg-wine-700 transition-colors" data-id="element-544">
                  Visit Chateau de Chaintres
                  <span className="ml-2" data-id="element-545">→</span>
                </a>
              </div>
            </motion.div>
          </div>

          {/* Wine Selection from Chateau de Chaintres */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="mt-20" data-id="element-546">
            <h3 className="font-playfair text-3xl font-bold text-charcoal mb-8 text-center" data-id="element-547">
              Our Selection from Chateau de Chaintres
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-548">
              {[{
              name: 'Saumur Champigny',
              variety: 'Cabernet Franc',
              price: '$21',
              image: 'https://cdn.magicpatterns.com/uploads/jjQYV3XY2yBUSWDCGJJ5m5/Screenshot_2026-03-23_at_8.45.35_PM.png',
              badge: 'Signature Red'
            }, {
              name: 'Saumur Blanc',
              variety: 'Chenin Blanc',
              price: '$22',
              image: 'https://cdn.magicpatterns.com/uploads/8DxNiTzRKHiF4u4n4eaC5L/Screenshot_2026-03-23_at_9.09.11_PM.png',
              badge: 'Signature White',
              imageScale: 'scale-[1.3]'
            }, {
              name: 'Les Hirondelles',
              variety: 'Rosé',
              price: '$19',
              image: 'https://cdn.magicpatterns.com/uploads/cTccvparp2Z6bncj1faiiB/Screenshot_2026-03-23_at_8.56.30_PM.png',
              badge: 'Rosé'
            }, {
              name: 'Blanc de Noir',
              variety: 'Sparkling',
              price: '$25',
              image: 'https://cdn.magicpatterns.com/uploads/gevVDRX6R6AEyKTmRbKtjb/Screenshot_2026-03-23_at_9.07.43_PM.png',
              badge: 'Sparkling'
            }].map((wine, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className="bg-cream-50 rounded-sm overflow-hidden shadow-subtle border border-cream-200" data-id="element-549">
                  <div className="relative h-64 bg-gray-100 p-6 flex items-center justify-center" data-id="element-550">
                    {wine.badge && <div className="absolute top-4 left-4 bg-wine text-white text-xs font-bold uppercase tracking-wider py-1 px-3 rounded-sm z-10" data-id="element-551">
                        {wine.badge}
                      </div>}
                    <img src={wine.image} alt={wine.name} className={`h-full w-auto object-contain ${wine.imageScale || ''}`} data-id="element-552" />
                  </div>
                  <div className="p-6 text-center" data-id="element-553">
                    <div className="text-xs text-wine/70 font-semibold uppercase tracking-wider mb-2" data-id="element-554">
                      {wine.variety}
                    </div>
                    <h4 className="font-playfair text-xl font-bold text-charcoal mb-2" data-id="element-555">
                      {wine.name}
                    </h4>
                    <div className="text-lg font-bold text-charcoal" data-id="element-556">
                      {wine.price}
                    </div>
                  </div>
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Coming Soon Section */}
      <section className="py-24 bg-cream-50" data-id="element-557">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-558">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="text-center mb-12" data-id="element-559">
            <span className="text-wine font-semibold tracking-wider uppercase text-sm mb-4 block" data-id="element-560">
              Expanding Our Portfolio
            </span>
            <h2 className="font-playfair text-4xl md:text-5xl text-charcoal font-bold mb-6" data-id="element-561">
              Coming <span className="italic text-wine font-normal" data-id="element-562">Soon</span>
            </h2>
            <p className="text-lg text-brown/70 max-w-2xl mx-auto" data-id="element-563">
              We're expanding our partnerships to bring you exceptional wines
              from renowned regions around the world.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto" data-id="element-564">
            {[{
            country: 'Italy',
            region: 'Tuscany & Piedmont',
            description: 'Iconic Italian wines from historic estates in Tuscany and Piedmont.'
          }, {
            country: 'Spain',
            region: 'Rioja & Ribera del Duero',
            description: 'Bold, elegant Spanish wines from celebrated wine regions.'
          }, {
            country: 'California',
            region: 'Napa Valley & Sonoma',
            description: 'Premium California wines from world-renowned vineyards.'
          }].map((region, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} className="bg-white p-8 rounded-sm border border-cream-200 text-center relative overflow-hidden" data-id="element-565">
                <div className="absolute top-4 right-4 bg-gold text-charcoal text-xs font-bold uppercase tracking-wider py-1 px-3 rounded-sm" data-id="element-566">
                  Coming Soon
                </div>
                <GlobeIcon className="w-12 h-12 text-wine/30 mx-auto mb-4" data-id="element-567" />
                <h3 className="font-playfair text-2xl font-bold text-charcoal mb-2" data-id="element-568">
                  {region.country}
                </h3>
                <p className="text-wine font-medium text-sm mb-3" data-id="element-569">
                  {region.region}
                </p>
                <p className="text-brown/60 text-sm" data-id="element-570">{region.description}</p>
              </motion.div>)}
          </div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} className="text-center mt-12" data-id="element-571">
            <p className="text-brown/60 mb-4" data-id="element-572">
              Have a vineyard suggestion? We'd love to hear from you.
            </p>
            <a href="/design#quote" className="inline-flex items-center px-6 py-3 bg-wine text-white rounded-sm font-medium hover:bg-wine-700 transition-colors" data-id="element-573">
              Get in Touch
            </a>
          </motion.div>
        </div>
      </section>
    </main>;
}